import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Play, Quote, TrendingUp } from "lucide-react";
import { AVIS_CAPTURES, TESTIMONIALS } from "../../data/content";
import { Reveal, Eyebrow, slug } from "../motion";

// Carte d'invitation (fermeture du carrousel) — renvoie vers la prise de rendez-vous.
export const TestimonialInvite = () => (
  <Link
    to="/rendez-vous"
    className="group flex h-full min-h-[300px] flex-col justify-between border border-neon/30 bg-neon/[0.05] p-8 transition-colors hover:border-neon"
    data-testid="testimonial-invite"
  >
    <Quote className="h-6 w-6 text-neon/60" />
    <p className="py-8 font-serif text-2xl italic leading-snug text-cream">Le prochain témoignage ? Peut-être le vôtre.</p>
    <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neon-light">
      Parler de mon projet <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
    </span>
  </Link>
);

export const TestimonialCard = ({ t }) => (
  <figure className="flex h-full flex-col border border-white/10 bg-[#1D1327] p-8" data-testid={`temoignage-${slug(t.prenom)}`}>
    <Quote className="h-6 w-6 text-neon/60" />
    <blockquote className="mt-6 flex-1">
      <p className="font-serif text-xl italic leading-relaxed text-white/75">« {t.texte} »</p>
    </blockquote>
    {t.resultat && (
      <p className="mt-6 flex items-center gap-2 border-t border-white/10 pt-5 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-neon-light" data-testid={`temoignage-resultat-${slug(t.prenom)}`}>
        <TrendingUp className="h-4 w-4 shrink-0" /> {t.resultat}
      </p>
    )}
    <figcaption className="mt-6 flex items-center gap-4">
      {t.photo ? (
        <img src={t.photo} alt={`${t.prenom} — ${t.projet}`} className="h-12 w-12 rounded-full object-cover" />
      ) : (
        <span className="grid h-12 w-12 place-items-center rounded-full border border-neon/40 font-display text-lg text-neon">{t.prenom[0]}</span>
      )}
      <div>
        <p className="text-cream">{t.prenom}</p>
        {t.projet && <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-neon">{t.projet}</p>}
      </div>
    </figcaption>
    {t.video && (
      <a href={t.video} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neon-light hover:text-cream">
        <Play className="h-3.5 w-3.5" /> Voir le témoignage vidéo
      </a>
    )}
    {t.link && (
      <Link to={t.link.to} data-testid={`temoignage-link-${slug(t.prenom)}`} className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neon-light transition-colors hover:text-cream">
        {t.link.label} <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    )}
  </figure>
);

// Carrousel horizontal (scroll-snap) : témoignages réels + carte d'invitation.
export const TestimonialCarousel = () => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * Math.min(420, ref.current.clientWidth * 0.85), behavior: "smooth" });
  return (
    <div className="relative" data-testid="temoignages-carousel">
      <div ref={ref} className="-mx-6 flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 pb-4 sm:-mx-10 sm:px-10 lg:-mx-16 lg:px-16 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TESTIMONIALS.map((t) => (
          <div key={t.prenom} className="w-[85vw] max-w-[400px] shrink-0 snap-start sm:w-[400px]">
            <TestimonialCard t={t} />
          </div>
        ))}
        <div className="w-[85vw] max-w-[400px] shrink-0 snap-start sm:w-[400px]">
          <TestimonialInvite />
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <button onClick={() => scroll(-1)} aria-label="Témoignages précédents" data-testid="temoignages-prev" className="grid h-11 w-11 place-items-center border border-white/20 text-cream transition-colors hover:border-neon hover:text-neon">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <button onClick={() => scroll(1)} aria-label="Témoignages suivants" data-testid="temoignages-next" className="grid h-11 w-11 place-items-center border border-white/20 text-cream transition-colors hover:border-neon hover:text-neon">
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const Testimonials = () => (
  <section id="temoignages" className="scroll-mt-20 py-24 lg:py-36" data-testid="section-temoignages">
    <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-16">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <div>
          <Reveal>
            <Eyebrow>Ils témoignent</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.08] text-cream sm:text-5xl lg:text-6xl">Ils sont passés à l'action.</h2>
          </Reveal>
        </div>
        <Reveal delay={0.16}>
          <p className="max-w-sm text-sm leading-relaxed text-white/50">Des personnes qui n'avaient jamais investi, accompagnées de l'idée au lancement. Témoignages réels, publiés avec leur accord.</p>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="mt-14">
        <TestimonialCarousel />
      </Reveal>

      {AVIS_CAPTURES.length > 0 && (
        <Reveal delay={0.1}>
          <div className="mt-16">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">Captures réelles de nos annonces & avis voyageurs</p>
            <div className="mt-6 grid gap-6 md:grid-cols-3" data-testid="avis-captures">
              {AVIS_CAPTURES.map((c, i) => (
                <img key={i} src={c.image} alt={c.alt} className="aspect-[16/10] w-full border border-white/10 object-cover" loading="lazy" />
              ))}
            </div>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.15}>
        <div className="mt-16 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <Link to="/avis" data-testid="temoignages-link-avis" className="btn-outline">
            Voir tous les avis <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">Résultats publiés avec l'accord de nos clients</p>
        </div>
      </Reveal>
    </div>
  </section>
);
